import {isEmpty, isNumber, some} from 'lodash'
import {
  BasePlaybackSettings,
  Manifest,
  TrackItem,
  AudioTrack,
  TrackSprite,
  CaptionsTrack,
  Captions,
  DocVideo,
  Slide
} from 'powtoon-versioning/src/currentSchema'
/// @ts-expect-error need to migrate to ts
import {updateAnimationGraphsKeyframesTime} from 'powtoon-commons/models/utils/trackAnimationGraphs'
import {hasAnimationGraphs} from 'powtoon-commons/utils'
import {getAudioDurationFromManifest} from 'powtoon-commons/utils/audio'
import {isDecimalInteger, roundToInterval, FLOATING_POINT_TOLERANCE} from 'powtoon-commons/utils/numbers'
/// @ts-expect-error need to migrate to ts
import {updatePlaybackSettings} from 'powtoon-commons/models/utils/videoPlaybackSettings'
import {getVideoDuration, getSoundLength} from 'powtoon-commons/models/utils/mediaObject'
import {
  getLipSyncPlaybackSettingsFromSprite,
  isAvatarItem,
  isVideoItem,
  getLipSyncAudioTrackFromSprite,
  isImageHolderItemWithVideoContent,
  isImageHolderItemWithAvatarContent,
  isAudioTrackWithStartAndEndTime
} from 'powtoon-document'
import {MINIMUM_STEP_SIZE} from 'powtoon-document/consts/duration'
import {getFirstSprite, getSpriteOrSpriteContent} from 'powtoon-document/utils/trackSpriteUtils'
import {updateItemsByFilter, removeItems} from 'powtoon-commons/utils/mutableHelpers'

export const CAPTION_MIN_TIME_DURATION = MINIMUM_STEP_SIZE
export const AUDIO_TRACKS_MIN_TIME_DURATION = MINIMUM_STEP_SIZE

export interface PlaybackUpdateOptions {
  preserveEndTime?: boolean,
  shiftStartTimeOnTrim?: boolean
}

export function getUpdatedTrackProps(
  track: TrackItem,
  startTime: number,
  endTime: number,
  manifestObject?: Manifest,
  playbackOptions: PlaybackUpdateOptions = {}
) {
  const animationGraphs = hasAnimationGraphs(track) ?
    {
      animationGraphs: updateAnimationGraphsKeyframesTime(track.animationGraphs, startTime, endTime, null, track)
    } :
    {}

  const sprite = getFirstSprite(track) as TrackSprite
  const isVideoInFrame = isImageHolderItemWithVideoContent(sprite)
  const spriteContent = getSpriteOrSpriteContent(track)
  
  if (isVideoItem(spriteContent)) {
    const videoDurationFromManifest = getVideoDuration(manifestObject, spriteContent.contentItemId)
    const videoDuration = isDecimalInteger(videoDurationFromManifest)
      ? videoDurationFromManifest
      : roundToSupportedPrecision(videoDurationFromManifest)


    const {adjustedEndTime, adjustedStartTime} = adjustPlaybackSettingsTrackLength({
      endTime,
      startTime,
      track,
      mediaItemDuration: videoDuration,
      playbackSettings: spriteContent.playbackSettings
    })

    const playbackSettings = updatePlaybackSettings({
      track,
      trackNewStartTime: adjustedStartTime,
      trackNewEndTime: adjustedEndTime,
      videoDuration,
      playbackSettings: spriteContent.playbackSettings,
      playbackOptions
    })
    const adjustedSprites = isVideoInFrame ?
      [{...sprite, content: {...sprite.content, playbackSettings}}] :
      [{...sprite, playbackSettings}]

    return  {
      ...animationGraphs,
      sprites: adjustedSprites,
      startTime: adjustedStartTime,
      endTime: adjustedEndTime
    }
  }
  const audioTrack = getLipSyncAudioTrackFromSprite(spriteContent) as AudioTrack
  if (audioTrack) {
    /*
    Explanation of the character lipSync document structure

    track : {
      startTime: 4,
      endTime: 9
    Sprites: [{
      itemType: 'fyber',
      
      audioTracks: [{
         audioType: 'voiceover',
         startTime: 0,
         endTime: 5,
         playbackSettings: {
            startTime: 3,
            endTime: 15
         }
      }]
    }]
  }
    We have 3 startTime,endTime pairs:
    * On the track itself - this is for the character on stage. In the example above - from second 4 to 9.
    * On the audioTrack - this is to represent any offsets, if we want the character to start talking or stop talking
    with some offset to the character's appearance. Currently it should be always 0 for the
    startTime and 'character duration' for the endTime. (e.g. for a character showing on the screen for 5 seconds,
    from 4 to 9 - startTime=0, endTime=5)..
    Yes - even if the audio file is shorter than the character duration
    (since in the future - we might want to support playing the sound next to objects in a loop
      e.g. a motor sound or an ambulance, etc...)
    * On the playbackSettings - this is for playing just a part of the audio. E.g. if the recording has some
      silence at the beginning,and we want the audio to start playing from second 3 of the recording.
      The endTime should be the 'audio duration', unless we trim it from the end too...
      so even if the character is showing for 5 seconds and the audio is 15 seconds long, the endTime will be 15.
    */
    
    const playbackSettings = getLipSyncPlaybackSettingsFromSprite(spriteContent)
    const soundLength = getSoundLength(manifestObject, audioTrack?.contentItemId)

    const {
      startTime: adjustedPlaybackSettingsStartTime,
      endTime: adjustedPlaybackSettingsEndTime
    } = updatePlaybackSettings({
      track,
      trackNewStartTime: startTime,
      trackNewEndTime: endTime,
      videoDuration: soundLength,
      playbackSettings,
      isVoiceoverLogicActive: true
    })
    const adjustedAudioTrack = {
      ...audioTrack
    }
    const adjustedPlaybackSettings = {
      ...playbackSettings,
      startTime: adjustedPlaybackSettingsStartTime,
      endTime: adjustedPlaybackSettingsEndTime
    }
    
    const characterDuration = roundToSupportedPrecision(endTime - startTime)
    adjustedAudioTrack.startTime = 0
    adjustedAudioTrack.endTime = characterDuration
    
    const adjustedSprites = adjustSpritesWithLipsync(
      sprite,
      adjustedPlaybackSettings,
      adjustedAudioTrack
    )
    
    return  {
      ...animationGraphs,
      sprites: adjustedSprites,
      startTime: roundToSupportedPrecision(startTime),
      endTime: roundToSupportedPrecision(endTime)
    }
  }
  
  return {
    ...animationGraphs,
    startTime: roundToSupportedPrecision(startTime),
    endTime: roundToSupportedPrecision(endTime)
  }
}

export function getMinTrackDuration(track: TrackItem): number {
  const sprite = getFirstSprite(track) as TrackSprite
  const spriteContent = getSpriteOrSpriteContent(track)

  if (isVideoItem(spriteContent) || isImageHolderItemWithVideoContent(sprite)) {
    return MINIMUM_STEP_SIZE
  }
  const audioTrack = getLipSyncAudioTrackFromSprite(spriteContent)
  if (audioTrack) {
    return MINIMUM_STEP_SIZE
  }
  return 0
}

export const getItemsOutsideNewDuration = <T extends {startTime: number, endTime: number}>(
  items: T[] | undefined,
  duration: number,
  durationDiff: number,
  isStartTime: boolean,
  minItemDuration: number | ((item: T) => number) = 0
): T[] => {
  if (durationDiff >= 0 || !items?.length) {
    return []
  }

  const cutPoint = isStartTime ? -durationDiff : (duration + durationDiff)

  return items.filter((item) => {
    const limit = typeof minItemDuration === 'function' ? minItemDuration(item) : minItemDuration

    if (isStartTime) {
      return item.endTime <= roundToSupportedPrecision(cutPoint + limit)
    }
    return item.startTime >= roundToSupportedPrecision(cutPoint - limit)
  })
}

export const getTracksOutsideNewSlideDuration = (
  items: TrackItem[] | undefined,
  duration: number,
  durationDiff: number,
  isStartTime: boolean
): TrackItem[] => getItemsOutsideNewDuration(items, duration, durationDiff, isStartTime, getMinTrackDuration)

export const getAudioTracksOutsideNewSlideDuration = (
  items: AudioTrack[] | undefined,
  duration: number,
  durationDiff: number,
  isStartTime: boolean
): AudioTrack[] => getItemsOutsideNewDuration(
  items, duration, durationDiff, isStartTime, AUDIO_TRACKS_MIN_TIME_DURATION
)

export const getCaptionsTrackOutsideNewSlideDuration = (
  items: CaptionsTrack[] | undefined,
  duration: number,
  durationDiff: number,
  isStartTime: boolean
): CaptionsTrack[] => getItemsOutsideNewDuration(
  items, duration, durationDiff, isStartTime, CAPTION_MIN_TIME_DURATION
)

export const getIsCaptionsOutsideNewSlideDuration = (
  captions: Captions | undefined,
  duration: number,
  durationDiff: number,
  isStartTime: boolean
): boolean => some(
  captions,
  (captionTracks) => !isEmpty(
    getCaptionsTrackOutsideNewSlideDuration(captionTracks, duration, durationDiff, isStartTime)
  )
)

export const adjustSpritesWithLipsync = (
  sprite: TrackSprite,
  playbackSettings: BasePlaybackSettings,
  audioTrack: AudioTrack
) => {
  if (isAvatarItem(sprite)) {
    return [{
      ...sprite,
      playbackSettings,
      audioTracks: [audioTrack]
    }]
  } else if (isImageHolderItemWithAvatarContent(sprite)) {
    return [{
      ...sprite,
      content: {
        ...sprite.content,
        playbackSettings,
        audioTracks: [audioTrack]
      }
    }]
  }
  return [{
    ...sprite,
    audioTracks: [{...audioTrack, playbackSettings}]
  }]
}

export function getUpdatedAudioTrackProps(
  audioTrack: AudioTrack,
  trackNewStartTime: number,
  trackNewEndTime: number,
  manifestObject: Manifest
) {
  const {playbackSettings: currentPlaybackSettings, contentItemId} = audioTrack
  const audioDuration = getAudioDurationFromManifest(
    contentItemId,
    manifestObject,
    roundToSupportedPrecision,
    MINIMUM_STEP_SIZE
  )
  const playbackSettings = updatePlaybackSettings({
    track: audioTrack,
    trackNewStartTime,
    trackNewEndTime,
    videoDuration: audioDuration,
    playbackSettings: currentPlaybackSettings
  })
  
  if (isAudioTrackWithStartAndEndTime(audioTrack)) {
    if (playbackSettings.startTime < 0) {
      playbackSettings.startTime = 0
    }
    if (playbackSettings.endTime > audioDuration) {
      playbackSettings.endTime = audioDuration
    }
    if (playbackSettings.endTime < playbackSettings.startTime) {
      playbackSettings.endTime = playbackSettings.startTime + MINIMUM_STEP_SIZE
    }
    
    const {adjustedEndTime, adjustedStartTime} = adjustPlaybackSettingsTrackLength({
      endTime: trackNewEndTime,
      startTime: trackNewStartTime,
      track: audioTrack,
      mediaItemDuration: audioDuration,
      playbackSettings: currentPlaybackSettings
    })
  
    const startTime = roundToSupportedPrecision(Math.max(adjustedStartTime, 0))
    let endTime = roundToSupportedPrecision(Math.max(adjustedEndTime, startTime + MINIMUM_STEP_SIZE))
  
    const requestedDuration = endTime - startTime
    const maxAllowedDuration = Math.min(requestedDuration, audioDuration)
    const newDuration = Math.max(maxAllowedDuration, MINIMUM_STEP_SIZE)

    endTime = roundToSupportedPrecision(startTime + newDuration)
    playbackSettings.endTime = roundToSupportedPrecision(playbackSettings.startTime + newDuration)

    return  {
      ...audioTrack,
      playbackSettings,
      startTime,
      endTime
    }
  }
}

const getAdjustedStartTime = (
  currentStartTime: number,
  nextStartTime: number,
  psStartTime: number,
  videoDuration: number
) => {
  const deltaStartTime = Math.abs(currentStartTime - nextStartTime)
  const calculatedStartTime = currentStartTime > nextStartTime
    ? psStartTime - deltaStartTime
    : psStartTime + deltaStartTime

  const minTime = calculatedStartTime > 0
    ? currentStartTime - videoDuration
    : currentStartTime - Math.abs(calculatedStartTime + deltaStartTime)

  return Math.max(nextStartTime, minTime)
}

const getIsTrackMovedOnly = (
  currentStartTime: number,
  currentEndTime: number,
  nextStartTime: number,
  nextEndTime: number
) => {
  // true if track was moved between different start and end times, false if duration was changed in any way
  const ratioChange = Math.abs(nextStartTime - currentStartTime) - Math.abs(nextEndTime - currentEndTime)
  return Math.abs(ratioChange) < 0.00001
}

interface AdjustVideoPlaybackSettingsTrackLengthArgs {
  endTime: number,
  startTime: number,
  track: TrackItem,
  manifestObject?: Manifest
  currentTime: number
}

export function adjustVideoPlaybackSettingsTrackLength({
  endTime: nextEndTime,
  startTime: nextStartTime,
  track,
  currentTime,
  manifestObject
}: AdjustVideoPlaybackSettingsTrackLengthArgs) {
  const sprite = getSpriteOrSpriteContent(track) as DocVideo
  const {startTime: trackCurrentStartTime, endTime: trackCurrentEndTime} = track
  const {contentItemId, playbackSettings: {startTime: psStartTime}} = sprite
  const videoDuration = getVideoDuration(manifestObject, contentItemId)

  let adjustTo = currentTime
  const isTrackMovedOnly = getIsTrackMovedOnly(trackCurrentStartTime, trackCurrentEndTime, nextStartTime, nextEndTime)

  if (isTrackMovedOnly && currentTime > nextStartTime && currentTime < nextEndTime) {
    adjustTo = currentTime
  } else if (!isTrackMovedOnly && nextEndTime > trackCurrentEndTime) {
    const maxTime = nextStartTime + videoDuration - psStartTime
    adjustTo = Math.min(nextEndTime, maxTime)
  } else if (
    (!isTrackMovedOnly && nextEndTime < trackCurrentEndTime) ||
    (isTrackMovedOnly && currentTime > nextEndTime)
  ) {
    adjustTo = nextEndTime
  } else if (!isTrackMovedOnly && nextStartTime < trackCurrentStartTime) {
    adjustTo = getAdjustedStartTime(trackCurrentStartTime, nextStartTime, psStartTime, videoDuration)
  } else if (
    (!isTrackMovedOnly && nextStartTime > trackCurrentStartTime) ||
    (isTrackMovedOnly && currentTime < nextStartTime)
  ) {
    adjustTo = nextStartTime
  }

  return roundToSupportedPrecision(adjustTo)
}

interface AdjustPlaybackSettingsTrackLengthArgs {
  endTime: number,
  startTime: number,
  mediaItemDuration: number,
  track: TrackItem | AudioTrack,
  playbackSettings: BasePlaybackSettings
}

export function adjustPlaybackSettingsTrackLength({
  endTime: nextEndTime,
  startTime: nextStartTime,
  track: {endTime: trackCurrentEndTime, startTime: trackCurrentStartTime},
  mediaItemDuration,
  playbackSettings: {startTime: psStartTime}
}: AdjustPlaybackSettingsTrackLengthArgs) {
  const returnValue = {
    adjustedEndTime: nextEndTime,
    adjustedStartTime: nextStartTime
  }
  if (isNumber(trackCurrentStartTime) && isNumber(trackCurrentEndTime)) {
    const isTrackMovedOnly = getIsTrackMovedOnly(trackCurrentStartTime, trackCurrentEndTime, nextStartTime, nextEndTime)

    if (!isTrackMovedOnly && Math.abs(nextEndTime - trackCurrentEndTime) > FLOATING_POINT_TOLERANCE) {
      const maxTime = nextStartTime + mediaItemDuration - psStartTime
      returnValue.adjustedEndTime = Math.min(nextEndTime, maxTime)
    } else if (!isTrackMovedOnly && nextStartTime < trackCurrentStartTime && nextEndTime >= trackCurrentEndTime) {
      returnValue.adjustedStartTime = getAdjustedStartTime(
        trackCurrentStartTime,
        nextStartTime,
        psStartTime,
        mediaItemDuration
      )
    } else if (!isTrackMovedOnly && nextEndTime < trackCurrentEndTime) {
      returnValue.adjustedEndTime = nextEndTime
    }
    if (returnValue.adjustedEndTime - returnValue.adjustedStartTime < MINIMUM_STEP_SIZE) {
      if (nextEndTime < trackCurrentEndTime) {
        returnValue.adjustedEndTime = returnValue.adjustedStartTime + MINIMUM_STEP_SIZE
      } else if (nextStartTime > trackCurrentStartTime) {
        returnValue.adjustedStartTime = returnValue.adjustedEndTime - MINIMUM_STEP_SIZE
      }
    }

    returnValue.adjustedEndTime = roundToSupportedPrecision(returnValue.adjustedEndTime)
    returnValue.adjustedStartTime = roundToSupportedPrecision(returnValue.adjustedStartTime)
  }
  return returnValue
}

export function adjustAudioTracksDuration( // mutates
  audioTrack: AudioTrack,
  durationDiff: number,
  newSlideDuration: number,
  manifestObject: Manifest,
  anchorPointTime: number = 0,
  shiftStartTimeOnTrim?: boolean
): void {
  const {
    startTime: oldStartTime,
    endTime: oldEndTime,
    contentItemId,
    playbackSettings: {startTime: oldAudioStartTime}
  } = audioTrack

  let startTime = Math.max(oldStartTime ?? 0, 0)
  const minEndTime = roundToSupportedPrecision(startTime + MINIMUM_STEP_SIZE)
  let endTime = Math.max(oldEndTime ?? minEndTime, minEndTime)
  let audioStartTime = Math.max(oldAudioStartTime ?? 0, 0)
  const oldTrackDuration = roundToSupportedPrecision(endTime - startTime)

  const maxAudioDuration = getAudioDurationFromManifest(
    contentItemId,
    manifestObject,
    roundToSupportedPrecision,
    MINIMUM_STEP_SIZE
  )
  const audioDurationLimit = Math.max(0, maxAudioDuration - audioStartTime)

  const isStartChange = shiftStartTimeOnTrim || (startTime > anchorPointTime)

  if (isStartChange) {
    startTime = roundToSupportedPrecision(Math.max(startTime + durationDiff, 0))
  }

  endTime = roundToSupportedPrecision(Math.max(endTime + durationDiff, startTime + MINIMUM_STEP_SIZE))

  if (endTime > newSlideDuration) {
    endTime = newSlideDuration
  }

  const maxEndTimeByAudioDuration = roundToSupportedPrecision(startTime + audioDurationLimit)
  if (endTime > maxEndTimeByAudioDuration) {
    endTime = maxEndTimeByAudioDuration
  }
  if ((endTime - startTime) < MINIMUM_STEP_SIZE) {
    startTime = Math.max(0, roundToSupportedPrecision(endTime - MINIMUM_STEP_SIZE))
  }

  const newTrackDuration = endTime - startTime
  const durationDelta = roundToSupportedPrecision(oldTrackDuration - newTrackDuration)
  if (shiftStartTimeOnTrim && durationDelta > 0) {
    audioStartTime = roundToSupportedPrecision(audioStartTime + durationDelta)
  }

  if (startTime !== oldStartTime || endTime !== oldEndTime || audioStartTime !== oldAudioStartTime) {
    audioTrack.startTime = startTime
    audioTrack.endTime = endTime
    audioTrack.playbackSettings.startTime = audioStartTime
    audioTrack.playbackSettings.endTime = roundToSupportedPrecision(audioStartTime + (endTime - startTime))
  }
}

export const roundToSupportedPrecision = (time: number, interval: number = MINIMUM_STEP_SIZE) => {
  return roundToInterval(time, interval)
}

export interface SlideDurationRange {
    startTime: number,
    endTime: number
}

export const getSlidesDurationRanges = (slides: Slide[]): SlideDurationRange[] => {
  let currentTime = 0

  return slides.map((slide) => {
    const startTime = currentTime
    const endTime = roundToSupportedPrecision(startTime + slide.duration)
    currentTime = endTime

    return {startTime, endTime}
  })
}

export const calcDocumentDuration = (slides: Slide[]) => {
  return slides.reduce((acc, slide) => {
    return acc + slide.duration
  }, 0)
}

export const updateCaptionsByNewSlideDuration = (
  slide: Slide,
  newDuration: number,
  duration: number,
  durationDiff: number,
  shiftStartTimeOnTrim: boolean = false
) => {
  if (!isEmpty(slide.captions) && (newDuration < slide.duration)) {
    for (const key in slide.captions) {
      const localizedCaptions = slide.captions[key]
      if (localizedCaptions && !isEmpty(localizedCaptions)) {

        const removeCaptionsTracks =
            getCaptionsTrackOutsideNewSlideDuration(localizedCaptions, duration, durationDiff, shiftStartTimeOnTrim)
        if (!isEmpty(removeCaptionsTracks)) {
          removeItems(localizedCaptions, removeCaptionsTracks)
        }

        updateItemsByFilter<CaptionsTrack>(
          localizedCaptions,
          ({startTime, endTime}) => endTime >= newDuration && startTime < newDuration,
          (caption) => caption.endTime = newDuration
        )
      }
    }
  }
}

export const updateAudioTracksByNewSlideDuration = (
  audioTracks: AudioTrack[] | undefined,
  newSlideDuration?: number,
  slideDuration?: number,
  manifest?: Manifest,
  anchorPointTime: number = 0,
  shiftStartTimeOnTrim?: boolean
) => {
  if (!manifest || !newSlideDuration || !slideDuration || newSlideDuration === slideDuration || isEmpty(audioTracks)) {
    return
  }
  const durationDiff = roundToSupportedPrecision(newSlideDuration - slideDuration)
  updateItemsByFilter(
      audioTracks!,
      ({startTime, endTime}) => (startTime > anchorPointTime) ||
          (durationDiff < 0 && (shiftStartTimeOnTrim || endTime > newSlideDuration)),
      track => adjustAudioTracksDuration(
        track, durationDiff, newSlideDuration, manifest, anchorPointTime, shiftStartTimeOnTrim
      )
  )
}

